// angular
import { effect, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { tap } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
// services
import { StorageService } from '@shared/services/storage.service';
import { StateManagementService } from '@app/+chat/shared/services/state-management.service';
// types & interfaces
import { IUser } from '@app/shared/types/user.type';
import { IMeRes } from '@shared/interfaces/response/people/me.interface';
// enums & envs
import { I18n } from '../enums/i18n.enum';
import { AlertStatesEnum } from '@app/+chat/shared/enums/alert-states.enum';
import { environment } from '@environments/environment';
// utils
import parseJwt from '@app/shared/utils/jwt-parser';

@Injectable()
export class UserService {
  public userSignal = signal<IUser>({ lang: I18n.en });
  public isUserLoggedIn = signal<boolean>(false);
  public user$ = toObservable(this.userSignal);

  private _user!: IUser;

  // dependencies
  private readonly _http = inject(HttpClient);
  private readonly _storageService = inject(StorageService);
  private readonly _stateManagement = inject(StateManagementService);

  constructor() {
    this.user$.subscribe((user: IUser) => (this._user = user));

    effect(() => {
      this.isUserLoggedIn.set(
        (this.userSignal()?.roles?.length || 0) > 0 &&
          !!this.userSignal()?.mobile_verified
      );
    });
  }

  public updateUserSubject(updatedField: IUser): void {
    this.userSignal.set({ ...this.userSignal(), ...updatedField });
  }

  public async getToken() {
    let _token = this._storageService.getToken();
    if (_token) {
      this.createUserFromToken(_token);
      return _token;
    }
    return null;
  }

  clearUserSubject(): void {
    this.userSignal.set({ lang: I18n.en });
  }

  public getUserInfo() {
    return this._http.get<IMeRes>('main/people/get-info').pipe(
      tap((res: IMeRes) => {
        if (res) {
          const _user: IUser = {
            lang: environment.defaultLang,
            _id: res._id,
            profile_id: res.profile_id,
            kid: res.kid,
            date_of_birth: res.date_of_birth,
            title: res.title,
            address: res.address,
            location: res.location,
            job_position: res.job_position,
            links: res.links,
            type: res.type,
            country: res.country,
            province: res.province,
            city: res.city,
            profile_image: res.profile_image,
            signature_image: res.signature_image,
            seal_image: res.seal_image,
            first_name: res.first_name,
            last_name: res.last_name,
            email: res.email,
            mobile_verified: res.mobile_verified,
            enabled: res.enabled,
            groups: res.groups,
            clients: res.clients,
            username: res.username,
            business: res?.business,
            mobile: res?.mobile,
            roles: res.roles,
          };
          this.updateUserSubject(_user);

          // update chat state
          const chatAlreadyStarted =
            this._storageService.getStartedSupportChat();
          if (chatAlreadyStarted) {
            this._stateManagement.chatAlertState.set(
              AlertStatesEnum.supportChat
            );
          } else {
            this._stateManagement.chatAlertState.set(
              AlertStatesEnum.startChatAlert
            );
          }

          return _user;
        }
        return res;
      })
    );
  }

  public updateProfileLanguage(language: string) {
    return this._http
      .patch('um/profiles/change-language', {
        language,
      })
      .subscribe();
  }

  public createUserFromToken(token: string) {
    if (token) {
      const parsedToken = parseJwt(token);
      const _user = {
        id: parsedToken.sub,
        username: parsedToken.preferred_username || '',
        lang: this._user?.lang
          ? I18n[this._user?.lang]
          : I18n[environment.defaultLang],
        email: parsedToken.email,
        emailVerified: parsedToken.email_verified,
        // roles: parsedToken.resource_access
        //   ? Object.keys(parsedToken.resource_access)
        //       .map((key: string) => parsedToken.resource_access[key].roles)
        //       .flat()
        //   : [],
      };
      this.updateUserSubject(_user);
    }
  }

  public changeToBusiness() {
    return this._http.post<{ status: boolean }>(
      'main/people/change-to-business',
      {}
    );
  }

  public async logout() {
    this._storageService.removeToken();
    this._storageService.removeRefreshToken();
    this._storageService.removeFirebaseToken();
    this._storageService.removeRefreshToken();
    this._storageService.removeFirebaseToken();

    // remove chat info on logout
    this._storageService.removeItemFromStorage('chat-id');
    this._storageService.removeItemFromStorage('member-id');
    this._storageService.removeItemFromStorage('startedSupportChat');

    this.clearUserSubject();
    window.location.reload();
  }
}
